import {
  camelCase,
  type,
  range,
  binaryFind,
  isFunction,
  timeout,
} from "./utils";

export const addOneTimeListener = (
  el,
  eventName,
  fn,
  ignoreBubbling = false
) => {
  const listener = (event) => {
    if (ignoreBubbling && event.target !== el) return;

    el.removeEventListener(eventName, listener);
    fn(event);
  };
  el.addEventListener(eventName, listener);
};

/** Responds to the first event triggered. Useful for polyfilling webkit prefixed events. */
const eventRacePromise = (
  el,
  events,
  { timeout, matchTarget = false } = {}
) => {
  const eventsString = [].concat(events).join(" ");
  return new Promise((resolve) => {
    let callbackRun = false;
    const callback = (...args) => {
      if (callbackRun || (matchTarget && el !== args[0].target)) return;
      callbackRun = true;
      resolve(...args);
      off(el, { [eventsString]: callback });
    };

    on(el, { [eventsString]: callback });
    if (timeout != null) {
      setTimeout(() => callback({ message: "Promise timed out." }), timeout);
    }
  });
};

export const animationEndPromise = (el, options = {}) => {
  return eventRacePromise(el, "animationend webkitAnimationEnd", options);
};

export const transitionEndPromise = (el, options = {}) => {
  return eventRacePromise(el, "transitionend webkitTransitionEnd", options);
};

/**
 * Given:
 *   - `el` - a DOM node
 *   - `transitions` - an array where each element is either 1) a function, or 2) an object of style property
 *     names to values
 * this sequentially 1) runs the function and waits for the Promise returned by the function to resolve (if
 * the function returns a Promise) before moving onto the next step, or 2) applies the styles to `el` and
 * waits for a `transitionend` event before moving onto the next step. Returns a Promise. `timeoutMS` is
 * an optional duration (in ms) after which the next transition should be run even if the previous transition
 * has not completed or if the previous function's returned promise has not resolved.
 */
export const runTransitionSequence = (
  el,
  transitions,
  { timeoutMS = null, continueFn = () => true } = {}
) => {
  return transitions.reduce((prevTransitionEndPromise, thisTransition) => {
    return prevTransitionEndPromise.finally(() => {
      if (!continueFn()) {
        return Promise.resolve();
      }

      let currentTransitionEndPromise;
      if (isFunction(thisTransition)) {
        currentTransitionEndPromise = thisTransition();
      } else {
        currentTransitionEndPromise = transitionEndPromise(el);
        Object.assign(el.style, thisTransition);
      }

      if (timeoutMS == null) {
        return currentTransitionEndPromise;
      }

      return Promise.race([timeout(timeoutMS), currentTransitionEndPromise]);
    });
  }, Promise.resolve());
};

export const appendChildren = (node, children) => {
  const fragment = document.createDocumentFragment();

  children.forEach((child) => {
    if (!child) return;

    fragment.appendChild(child instanceof Node ? child : createElement(child));
  });

  node.appendChild(fragment);
};

export const copyToClipboard = (text, multiline) => {
  const elemType = multiline ? "textarea" : "input";
  const el = document.createElement(elemType);
  document.body.appendChild(el);
  el.value = text;
  el.select();
  document.execCommand("copy");
  el.remove();
};

/**
 * @param {Object} elementConfig A map of options specifying a new Element.
 * @param {String} [elementConfig.namespace = null] A namespace URI to associate with the new element.
 * See https://developer.mozilla.org/en-US/docs/Web/API/Document/createElementNS for details.
 * @param {String} [elementConfig.tagName = "div"] The tag name determining the result
 * Element's type. Defaults to "div".
 * @param {String[]} [elementConfig.classList = []] A list of classes. Falsy values
 * are ignored. Defaults to [].
 * @param {{}} [elementConfig.style = {}] A map of style settings to merge into the result
 * Element's style. Defaults to {}.
 * @param {{}} [elementConfig.attributes = {}] A map of attributes to set on the result
 * Element. Defaults to {}.
 * @param {{}} [elementConfig.data = {}] A map of data to merge into the result Element's
 * dataset. Defaults to {}.
 * @param {({}|Element)[]} [elementConfig.children = []] An array of Elements or
 * elementConfigs to append as children. Falsy values are ignored. Defaults to [].
 * @param {{}} [elementConfig.eventHandlers = {}] A map from event names to handlers,
 * which will all be assigned to listen on the result Element. Defaults to {}.
 * @returns {HTMLElement} An Element object with properties corresponding to the parameters.
 */
export const createElement = (elementConfig) => {
  if (type(elementConfig) === "string" || type(elementConfig) === "number") {
    return document.createTextNode(elementConfig);
  }

  const {
    namespace = null,
    tagName = "div",
    classList = [],
    style = {},

    attributes = {},
    data = {},

    children = [],

    eventHandlers = {},
    ...unintendedProps
  } = elementConfig;

  const unintendedKeys = Object.keys(unintendedProps);
  if (unintendedKeys.length > 0) {
    throw new Error(
      `\`createElement\` must not receive unintended keys: ${JSON.stringify(
        unintendedKeys
      )}`
    );
  }

  const element =
    namespace == null
      ? document.createElement(tagName)
      : document.createElementNS(namespace, tagName);
  const filteredClassList = (classList || []).filter((className) => className);
  if (filteredClassList.length > 0) {
    element.classList.add(...filteredClassList);
  }
  Object.assign(element.style, style);

  Object.entries(attributes).forEach(([name, value]) => {
    element.setAttribute(name, value);
  });
  Object.assign(element.dataset, data);

  if (children.length > 0) {
    appendChildren(element, children);
  }

  on(element, eventHandlers);

  return element;
};

export const empty = (node) => {
  while (node.firstChild) {
    node.removeChild(node.firstChild);
  }

  return node;
};

export const fetchHTML = (url) => {
  return fetch(url)
    .then((response) => response.text())
    .then(strToElement);
};

export const firstAncestor = (node, selector, halt = "body") => {
  while (node != null && !node.matches(selector)) {
    if (node.matches(halt)) return null;

    node = node.parentNode;
  }

  return node;
};

/**
 * Returns a DocumentFragment containing the given HTML string, or null if
 * the string is empty or falsey.
 */
export const fragmentFromString = (strHTML) => {
  if (!strHTML) {
    return null;
  }

  const templateEl = document.createElement("template");
  templateEl.innerHTML = strHTML;
  return templateEl.content;
};

// Forces a reflow of the element so that cached styles are recomputed. This is
// important for ensuring that animations are used when changing styles or CSS
// classes via JS. See http://stackoverflow.com/q/11131875.
export const forceReflow = (el) => {
  el.offsetHeight;
};

export const generateSnippetMap = (html) => {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = html;
  const snippets = [...wrapper.querySelectorAll("[data-snippet]")].reduce(
    (coll, node) => {
      const name = node.dataset.snippet;
      coll[name] = node;
      node.removeAttribute("data-snippet");
      node.remove();

      return coll;
    },
    {}
  );

  return (name) => {
    if (snippets[name] == null) return;

    return snippets[name].cloneNode(true);
  };
};

export const getCSSProperty = (node, property) => {
  return window.getComputedStyle(node).getPropertyValue(property);
};

export const getElementsByClass = (parent, classNames) => {
  return classNames.reduce((elements, name) => {
    elements[camelCase(name)] = parent.querySelector(`.${name}`);

    return elements;
  }, {});
};

// A version of `getBoundingClientRect` relative to a reference node instead
// of the window.
export const getRelativeBoundingClientRect = (node, reference = null) => {
  if (reference == null) {
    reference = node.parentNode;
  }

  const rect = node.getBoundingClientRect();
  const refRect = reference.getBoundingClientRect();
  return {
    top: rect.top - refRect.top,
    bottom: rect.bottom - refRect.bottom,
    left: rect.left - refRect.left,
    right: rect.right - refRect.right,
    width: rect.width,
    height: rect.height,
  };
};

// Determines a bounding rectangle that encompasses all given nodes.
export const getAggregateBoundingClientRect = (nodes) => {
  return [...nodes].reduce(
    (agg, node) => {
      const rect = node.getBoundingClientRect();
      agg.top = Math.min(agg.top, rect.top);
      agg.bottom = Math.max(agg.bottom, rect.bottom);
      agg.left = Math.min(agg.left, rect.left);
      agg.right = Math.max(agg.right, rect.right);
      agg.width = Math.max(agg.width, rect.left - agg.left + rect.width);
      agg.height = Math.max(agg.height, rect.top - agg.top + rect.height);
      return agg;
    },
    {
      top: Infinity,
      bottom: 0,
      left: Infinity,
      right: 0,
      width: 0,
      height: 0,
    }
  );
};

// Given an element, this will return an integer that is the maximum font size (in px) that allows all the
// text to fit without overflow.
export const getFontSizeToFit = (el, maxFontSizePx = 24) => {
  const startingFontSize = el.style.fontSize;
  const startingOverflow = el.style.overflow;
  el.style.overflow = "hidden";
  let high = maxFontSizePx + 1;
  let low = 0;
  el.style.fontSize = `${high}px`;
  while (low + 1 < high) {
    const mid = low + ((high - low) >> 1);
    el.style.fontSize = `${mid}px`;
    if (el.clientHeight < el.scrollHeight || el.clientWidth < el.scrollWidth) {
      high = mid;
    } else {
      low = mid;
    }
  }

  el.style.fontSize = startingFontSize;
  el.style.overflow = startingOverflow;
  return low;
};

/**
 * Attempt to calculate the `font-size` needed to have the text of `el` fit within the given number of lines.
 *
 * This is far from a perfect solution as there are several factors that determine line count not to mention
 * occasional ambiguity of what constitutes a "line" when there's `innerHTML` to contend with.
 *
 * @param {HTMLElement} el - Element with text to fit `maxLines`.
 * @param {number} maxLines - Max line count to calculate for.
 * @param {number} [precision] - The amount in px to incrementally adjust `font-size` by to determine changes.
 * @return {number} - In px.
 */
export const getFontSizeToFitLines = (el, maxLines, precision = 0.5) => {
  /**
   * NOTE(oscar): The computed style of certain "older" properties should always be in px regardless of
   * applied style.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/getComputedStyle
   */
  const fontSize = parseFloat(getCSSProperty(el, "font-size"));

  const prevFontSizeStyle = el.style.fontSize;
  const prevHeight = el.style.height;
  const prevLineHeight = el.style.lineHeight;
  el.style.height = "unset";

  // NOTE(oscar): Ensures line-height is sensitive to changes in font-size.
  el.style.lineHeight = "10em";

  const computedFontSize =
    binaryFind(range(precision, fontSize, precision), (value) => {
      el.style.fontSize = `${value + precision}px`;
      const lineHeight = parseFloat(getCSSProperty(el, "line-height"));
      const paddingTop = parseFloat(getCSSProperty(el, "padding-top"));
      const paddingBottom = parseFloat(getCSSProperty(el, "padding-bottom"));
      const textHeight =
        el.getBoundingClientRect().height - paddingTop - paddingBottom;
      return textHeight > lineHeight * maxLines;
    }) || fontSize;

  el.style.height = prevHeight;
  el.style.fontSize = prevFontSizeStyle;
  el.style.lineHeight = prevLineHeight;
  return computedFontSize;
};

export const indexOf = (node) => {
  return [].indexOf.call(node.parentNode.children, node);
};

// Returns a Promise that resolves when the passed Image is rendered (determined by polling for a non-zero
// clientWidth and clientHeight).
export const imageIsRendered = (img, timeout = 10000) => {
  const start = Date.now();
  return new Promise((resolve, reject) => {
    const checkRendered = () => {
      if (Date.now() - start >= timeout) {
        reject(new Error(`Timeout loading image "${img.src}"`));
      } else if (img.clientWidth === 0 || img.clientHeight === 0) {
        requestAnimationFrame(checkRendered);
      } else {
        resolve();
      }
    };

    requestAnimationFrame(checkRendered);
  });
};

export const off = (node, events) => {
  Object.keys(events).forEach((names) => {
    const listener = events[names];

    names
      .trim()
      .split(/\s+/)
      .forEach((name) => {
        node.removeEventListener(name, listener);
      });
  });
};

export const on = (node, events) => {
  Object.keys(events).forEach((names) => {
    const listener = events[names];

    names
      .trim()
      .split(/\s+/)
      .forEach((name) => {
        node.addEventListener(name, listener);
      });
  });
};

export const onAnimationsComplete = (els, callback) => {
  let animationsLeft = els.length;
  els.forEach((el) => {
    addOneTimeListener(el, "webkitAnimationEnd", () => {
      animationsLeft -= 1;
      if (animationsLeft === 0) {
        callback();
      }
    });
  });
};

// Returns a map of { units, value } for a CSS length property.
// The length must be a string with "px" or "%" units or a value of 0.
export const parseCSSLength = (length) => {
  if (length === 0 || length === "0") return { value: 0, units: "px" };

  const match = /(.+)(px|%)/.exec(length);
  if (!match) return null;

  const [_, value, units] = match;
  if (value == null || units == null) return null;

  return { units, value: Number(value) };
};

export const prependChild = (parentNode, node) => {
  parentNode.insertBefore(node, parentNode.children[0]);

  return parentNode;
};

export const replaceStyle = (element, ...newStyles) => {
  element.removeAttribute("style");
  Object.assign(element.style, ...newStyles);
};

export const scrollIntoView = (node) => {
  const rect = node.getBoundingClientRect();
  const pRect = node.parentNode.getBoundingClientRect();
  const topDiff = rect.top - pRect.top;
  const botDiff = rect.bottom - pRect.bottom;

  if (topDiff < 0 && botDiff > 0) {
    node.parentNode.scrollTop += (topDiff + botDiff) / 2;
  } else if (topDiff < 0) {
    node.parentNode.scrollTop += topDiff;
  } else if (botDiff > 0) {
    node.parentNode.scrollTop += botDiff;
  }
};

export const strToElement = (str) => {
  const div = document.createElement("div");
  div.innerHTML = str.replace(/\n\s*/g, "");

  return div.firstChild;
};

export const strToStyle = (str) => {
  const style = document.createElement("style");
  style.innerHTML = str;

  return style;
};

/** Writes `contentHTML` into `iframeEl`. */
export const writeToIframe = (iframeEl, contentHTML) => {
  iframeEl.contentWindow.document.open();
  iframeEl.contentWindow.document.write(contentHTML);
  iframeEl.contentWindow.document.close();
};

export const preparetagHTML = (tagHTML) => {
  const wrapperEl = document.createElement("div");
  wrapperEl.innerHTML = tagHTML;

  // Script tags must be reattached in order to run.
  [...wrapperEl.querySelectorAll("script")].forEach((originalScriptTag) => {
    const replacementScriptTag = document.createElement("script");
    [...originalScriptTag.attributes].forEach(
      ({ nodeName: name, nodeValue: value }) => {
        replacementScriptTag.setAttribute(name, value);
      }
    );
    replacementScriptTag.innerHTML = originalScriptTag.innerHTML;
    originalScriptTag.parentNode.replaceChild(
      replacementScriptTag,
      originalScriptTag
    );
  });

  return wrapperEl;
};

export const appendImpressionTags = (containerEl, impressionTags) => {
  impressionTags.forEach((tagHTML) => {
    const wrapper = preparetagHTML(tagHTML);
    containerEl.append(wrapper);
  });
};



// WEBPACK FOOTER //
// ./src/js/frontendlib/dom_utils.js